import { useRtlDirection } from '@/lib/hooks/helpers/useRtlDirection'
import { useGetReferralCode } from '@/lib/hooks/services/useGetReferralCode'
import useGlobalStore from '@/store/useGlobalStore'
import { Popover, PopoverTrigger } from '@radix-ui/react-popover'
import Image from 'next/image'
import { useEffect } from 'react'
import type { SubmitHandler } from 'react-hook-form'
import { useForm } from 'react-hook-form'
import { Button } from '../../_ui/Button'
import { Input } from '../../_ui/Input'
import { PopoverContent } from '../../_ui/Popover'
import Spinner from '../../_ui/Spinner'
import T, { useTranslate } from '../../Helpers/Translate'

type InputFormType = {
  inputReferralCode: string
}

const ReferralInput = () => {
  const {
    user: { refError, refID, customerType, refName },
    app: { isLoading },
    setApp,
    setUser,
  } = useGlobalStore()

  const { translate } = useTranslate()
  const { getReferralCode } = useGetReferralCode()
  const { isRtl } = useRtlDirection()

  const {
    register,
    handleSubmit,
    formState: { errors },
  } = useForm<InputFormType>()

  const handleGetReferralCode = async ({ referral }: { referral: string }) => {
    if (!referral) return

    setApp({ isLoading: true })

    await getReferralCode({ refID: referral })

    setApp({ isLoading: false })
  }

  const onSubmit: SubmitHandler<InputFormType> = async (data) => {
    await handleGetReferralCode({ referral: data.inputReferralCode })
  }

  useEffect(() => {
    if (customerType !== 'Customer' || refName !== null) {
      return
    }

    ;(async () => {
      if (refID && customerType === 'Customer' && refName === null) {
        await handleGetReferralCode({ referral: refID })
      }
    })()
  }, [refID, customerType, refName])

  const getInputError = () => {
    if (errors.inputReferralCode) {
      return errors.inputReferralCode.message
    }

    if (refError) {
      return 'invalid_referral_code'
    }

    return ''
  }

  return (
    // eslint-disable-next-line @typescript-eslint/no-misused-promises
    <form
      onSubmit={handleSubmit(onSubmit)}
      className={`flex w-full ${isRtl ? 'space-x-reverse' : ''} space-x-2`}
    >
      <Input
        {...register('inputReferralCode', {
          onChange: () => {
            setUser({ refError: false })
          },
          required: {
            value: true,
            message: 'this_field_is_required',
          },
        })}
        label={translate('enter_ref_code')}
        iconLeft={
          isLoading ? (
            <Spinner />
          ) : (
            <Popover>
              <PopoverTrigger className="pointer-events-auto">
                <span>
                  <Image
                    alt="Details about referred"
                    src="/icons/info.svg"
                    width={20}
                    height={20}
                  />
                </span>
              </PopoverTrigger>
              <PopoverContent className="p-5">
                <span className="text-primary">
                  <T>member_invite_code</T>
                </span>
                <p className="mt-2 text-xs">
                  <T>best_prices</T>
                </p>
              </PopoverContent>
            </Popover>
          )
        }
        error={translate(getInputError() || '')}
        iconRight={
          <Button
            type="submit"
            className="pointer-events-auto mr-[-5px] p-2"
            variant="icon"
          >
            <Image
              height={15}
              width={15}
              alt="Arrow"
              src="/icons/arrow-long-right-blue.svg"
            />
          </Button>
        }
      />
    </form>
  )
}

export default ReferralInput
